import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import SchedaRiassuntiva from "@/components/SchedaRiassuntiva";
import { Search } from "lucide-react";

const nowYear = new Date().getFullYear();
const annoLbl = (a) => `${a}/${String(a + 1).slice(-2)}`;

export default function SchedaAdminPage() {
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [anno, setAnno] = useState(nowYear);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // {cod, cognome, nome}

  useEffect(() => {
    api.get("/alunni-portal/scheda-config").then((r) => setEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const r = await api.put("/alunni-portal/scheda-config", { enabled: !enabled });
      setEnabled(!!r.data.enabled);
    } finally { setSaving(false); }
  };

  const cerca = useCallback(async () => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await api.get("/alunni", { params: { anno, search: q.trim(), limit: 15 } });
      setResults(r.data.data || []);
    } catch { setResults([]); } finally { setSearching(false); }
  }, [q, anno]);

  useEffect(() => {
    const t = setTimeout(cerca, 350);
    return () => clearTimeout(t);
  }, [cerca]);

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1200, margin: "0 auto" }} data-testid="scheda-admin-page">
      <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>Scheda Alunno — Portale</h2>
      <p style={{ color: "#64748b", fontSize: 14, marginBottom: 18 }}>
        Scheda riassuntiva per anno visibile nel Portale Alunni. Gli alunni vedono solo la propria; qui puoi consultarle tutte.
      </p>

      {/* Switch globale */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, background: "white", border: "1px solid #e3e8ef", borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, color: "#0f172a" }}>Visibilità nel Portale Alunni</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>{enabled ? "Attiva: gli alunni vedono la propria scheda." : "Non attiva: la sezione è nascosta nel portale."}</div>
        </div>
        <button data-testid="scheda-toggle-btn" onClick={toggle} disabled={saving}
          style={{ position: "relative", width: 64, height: 32, borderRadius: 999, border: "none", cursor: saving ? "wait" : "pointer",
            background: enabled ? "#16a34a" : "#cbd5e1", transition: "background .2s" }}>
          <span style={{ position: "absolute", top: 3, left: enabled ? 35 : 3, width: 26, height: 26, borderRadius: "50%", background: "white", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
        </button>
        <span style={{ fontWeight: 800, color: enabled ? "#16a34a" : "#94a3b8", minWidth: 90, textAlign: "right" }}>{enabled ? "ATTIVA" : "NON ATTIVA"}</span>
      </div>

      {/* Selettore alunno + anno */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 16 }}>
        <div style={{ position: "relative", flex: 1, minWidth: 260 }}>
          <Search size={16} style={{ position: "absolute", left: 10, top: 11, color: "#94a3b8" }} />
          <input data-testid="scheda-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca alunno per cognome, nome o codice…"
            style={{ width: "100%", padding: "9px 12px 9px 34px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }} />
        </div>
        <select data-testid="scheda-anno-select" value={anno} onChange={(e) => setAnno(Number(e.target.value))}
          style={{ padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14 }}>
          {[nowYear + 1, nowYear, nowYear - 1, nowYear - 2].map((y) => <option key={y} value={y}>{annoLbl(y)}</option>)}
        </select>
      </div>

      {q.trim() && !selected && (
        <div style={{ background: "white", border: "1px solid #e3e8ef", borderRadius: 8, marginBottom: 16, maxHeight: 260, overflowY: "auto" }}>
          {searching ? <div style={{ padding: 12, color: "#64748b" }}>Ricerca…</div> :
            results.length ? results.map((r) => (
              <div key={r.Cod} data-testid={`scheda-result-${r.Cod}`} onClick={() => { setSelected({ cod: r.Cod, cognome: r.Cog, nome: r.Nom }); setResults([]); }}
                style={{ padding: "9px 14px", cursor: "pointer", borderBottom: "1px solid #f1f5f9", fontSize: 14, display: "flex", justifyContent: "space-between" }}>
                <span><b>{r.Cog} {r.Nom}</b></span>
                <span style={{ color: "#94a3b8", fontFamily: "monospace" }}>cod {r.Cod} · {r.settore || ""} {r.classe || ""}</span>
              </div>
            )) : <div style={{ padding: 12, color: "#94a3b8" }}>Nessun alunno trovato.</div>}
        </div>
      )}

      {selected && (
        <div style={{ marginBottom: 12 }}>
          <button data-testid="scheda-clear-btn" onClick={() => { setSelected(null); setQ(""); }}
            style={{ padding: "6px 12px", border: "1px solid #cbd5e1", background: "white", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            ← Cambia alunno
          </button>
        </div>
      )}

      {selected ? (
        <SchedaRiassuntiva alunnoCod={selected.cod} anno={anno} />
      ) : (
        <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", border: "2px dashed #e2e8f0", borderRadius: 12 }}>
          Cerca e seleziona un alunno per visualizzarne la scheda.
        </div>
      )}
    </div>
  );
}
